/* The one decorative tint map (packages/ui/CLAUDE.md section C7, ADR-0092
 * amendment of 24 Sep 2026). `tint` is decorative identity and `tone` is
 * semantic: a part takes one or the other, and its type refuses both.
 * Categorical tints tell items apart where colour means nothing else; module
 * tints are the module identity ramp, for chrome parts only (Card, IconTile,
 * ModuleHome, ModuleLibraryCard, StatusDot legend dots). Every part with a
 * `tint` prop types it from here. Literal class strings so Tailwind sees
 * them. */

/** The categorical decorative scale `pb-tint-1` … `pb-tint-16`. Ordinal on
 *  purpose: the caller maps its own domain onto the indices. */
export type CategoricalTint =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16;

/** The module identity ramp `pb-module-*`. Chrome only, never a status. */
export type ModuleTint = "solve" | "vsm" | "hoshin" | "lama" | "studio";

export interface TintClasses {
  /** A small filled mark: StatusDot's solid dot and its ping ring. */
  readonly dot: string;
  /** The dot colour as a border: StatusDot's outline and dashed marks. */
  readonly dotBorder: string;
  /** A region fill at `--pbds-opacity-tint-fill`. */
  readonly fill: string;
  /** A border colour at `--pbds-opacity-tint-line`. */
  readonly line: string;
  /** A full-strength bar: a card's identity stripe. */
  readonly stripe: string;
}

/** A module tint also carries the channel of the generated `hover-tint`
 *  utility, for the tiles that tint on hover (Card tint). */
export interface ModuleTintClasses extends TintClasses {
  /** Sets `--pb-hover-tint` to the module's hue. */
  readonly hoverTint: string;
}

export type CategoricalTintMap = Readonly<Record<CategoricalTint, TintClasses>>;
export type ModuleTintMap = Readonly<Record<ModuleTint, ModuleTintClasses>>;

export const CATEGORICAL_TINTS: CategoricalTintMap = {
  1: {
    dot: "bg-pb-tint-1",
    dotBorder: "border-pb-tint-1",
    fill: "bg-pb-tint-1/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-1/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-1",
  },
  2: {
    dot: "bg-pb-tint-2",
    dotBorder: "border-pb-tint-2",
    fill: "bg-pb-tint-2/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-2/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-2",
  },
  3: {
    dot: "bg-pb-tint-3",
    dotBorder: "border-pb-tint-3",
    fill: "bg-pb-tint-3/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-3/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-3",
  },
  4: {
    dot: "bg-pb-tint-4",
    dotBorder: "border-pb-tint-4",
    fill: "bg-pb-tint-4/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-4/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-4",
  },
  5: {
    dot: "bg-pb-tint-5",
    dotBorder: "border-pb-tint-5",
    fill: "bg-pb-tint-5/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-5/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-5",
  },
  6: {
    dot: "bg-pb-tint-6",
    dotBorder: "border-pb-tint-6",
    fill: "bg-pb-tint-6/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-6/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-6",
  },
  7: {
    dot: "bg-pb-tint-7",
    dotBorder: "border-pb-tint-7",
    fill: "bg-pb-tint-7/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-7/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-7",
  },
  8: {
    dot: "bg-pb-tint-8",
    dotBorder: "border-pb-tint-8",
    fill: "bg-pb-tint-8/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-8/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-8",
  },
  9: {
    dot: "bg-pb-tint-9",
    dotBorder: "border-pb-tint-9",
    fill: "bg-pb-tint-9/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-9/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-9",
  },
  10: {
    dot: "bg-pb-tint-10",
    dotBorder: "border-pb-tint-10",
    fill: "bg-pb-tint-10/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-10/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-10",
  },
  11: {
    dot: "bg-pb-tint-11",
    dotBorder: "border-pb-tint-11",
    fill: "bg-pb-tint-11/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-11/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-11",
  },
  12: {
    dot: "bg-pb-tint-12",
    dotBorder: "border-pb-tint-12",
    fill: "bg-pb-tint-12/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-12/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-12",
  },
  13: {
    dot: "bg-pb-tint-13",
    dotBorder: "border-pb-tint-13",
    fill: "bg-pb-tint-13/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-13/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-13",
  },
  14: {
    dot: "bg-pb-tint-14",
    dotBorder: "border-pb-tint-14",
    fill: "bg-pb-tint-14/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-14/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-14",
  },
  15: {
    dot: "bg-pb-tint-15",
    dotBorder: "border-pb-tint-15",
    fill: "bg-pb-tint-15/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-15/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-15",
  },
  16: {
    dot: "bg-pb-tint-16",
    dotBorder: "border-pb-tint-16",
    fill: "bg-pb-tint-16/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-tint-16/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-tint-16",
  },
};

export const MODULE_TINTS: ModuleTintMap = {
  solve: {
    dot: "bg-pb-module-solve",
    dotBorder: "border-pb-module-solve",
    fill: "bg-pb-module-solve/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-module-solve/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-module-solve",
    hoverTint: "[--pb-hover-tint:var(--pb-module-solve)]",
  },
  vsm: {
    dot: "bg-pb-module-vsm",
    dotBorder: "border-pb-module-vsm",
    fill: "bg-pb-module-vsm/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-module-vsm/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-module-vsm",
    hoverTint: "[--pb-hover-tint:var(--pb-module-vsm)]",
  },
  hoshin: {
    dot: "bg-pb-module-hoshin",
    dotBorder: "border-pb-module-hoshin",
    fill: "bg-pb-module-hoshin/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-module-hoshin/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-module-hoshin",
    hoverTint: "[--pb-hover-tint:var(--pb-module-hoshin)]",
  },
  lama: {
    dot: "bg-pb-module-lama",
    dotBorder: "border-pb-module-lama",
    fill: "bg-pb-module-lama/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-module-lama/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-module-lama",
    hoverTint: "[--pb-hover-tint:var(--pb-module-lama)]",
  },
  studio: {
    dot: "bg-pb-module-studio",
    dotBorder: "border-pb-module-studio",
    fill: "bg-pb-module-studio/[calc(var(--pbds-opacity-tint-fill)*100%)]",
    line: "border-pb-module-studio/[calc(var(--pbds-opacity-tint-line)*100%)]",
    stripe: "bg-pb-module-studio",
    hoverTint: "[--pb-hover-tint:var(--pb-module-studio)]",
  },
};

/** A module tile's soft recipe: the AppLauncher tile (IconTile's module tint). */
export interface ModuleSoftClasses {
  /** The module's soft fill (`--pbds-opacity-soft`). */
  readonly fill: string;
  /** The module colour as a border at 30%. */
  readonly border: string;
  /** The module's text ramp, for a glyph. */
  readonly text: string;
}

export const MODULE_TINT_SOFT: Readonly<Record<ModuleTint, ModuleSoftClasses>> =
  {
    solve: {
      fill: "bg-pb-module-solve-soft",
      border: "border-pb-module-solve/30",
      text: "text-pb-module-solve-text",
    },
    vsm: {
      fill: "bg-pb-module-vsm-soft",
      border: "border-pb-module-vsm/30",
      text: "text-pb-module-vsm-text",
    },
    hoshin: {
      fill: "bg-pb-module-hoshin-soft",
      border: "border-pb-module-hoshin/30",
      text: "text-pb-module-hoshin-text",
    },
    lama: {
      fill: "bg-pb-module-lama-soft",
      border: "border-pb-module-lama/30",
      text: "text-pb-module-lama-text",
    },
    studio: {
      fill: "bg-pb-module-studio-soft",
      border: "border-pb-module-studio/30",
      text: "text-pb-module-studio-text",
    },
  };

/** A module library card's border on hover and focus-within, read from its
 *  `group` wrapper at 50% (ModuleLibraryCard's tint). */
export const MODULE_TINT_GROUP_BORDER: Readonly<Record<ModuleTint, string>> = {
  solve:
    "group-hover:border-pb-module-solve/50 group-focus-within:border-pb-module-solve/50",
  vsm: "group-hover:border-pb-module-vsm/50 group-focus-within:border-pb-module-vsm/50",
  hoshin:
    "group-hover:border-pb-module-hoshin/50 group-focus-within:border-pb-module-hoshin/50",
  lama: "group-hover:border-pb-module-lama/50 group-focus-within:border-pb-module-lama/50",
  studio:
    "group-hover:border-pb-module-studio/50 group-focus-within:border-pb-module-studio/50",
};

/** The classes for either kind of tint. */
export function tintClasses(tint: CategoricalTint | ModuleTint): TintClasses {
  return typeof tint === "number"
    ? CATEGORICAL_TINTS[tint]
    : MODULE_TINTS[tint];
}
